import { isAxiosError } from "axios";
import { apiClient } from "./api-client";
import type { DynamicFieldSaveRequest } from "./api-types";
import type {
  MergersAndAcquisitionsRoleGroup,
  MergersAndAcquisitionsRow,
  MergersAndAcquisitionsTeam,
} from "@/components/Workflow/QuickActions/DealTracker/deal-tracker-types";

/**
 * The two M&A widget flavours. The dashboard controller compares this value
 * literally ("Analyst" picks the first configured template pair, anything else
 * the second), so the casing matters.
 */
export const MERGERS_TRACKER_TYPES = ["Analyst", "Associate"] as const;

export type MergersTrackerType = (typeof MERGERS_TRACKER_TYPES)[number];

/**
 * The M&A dashboard answers "you have no widget" with a 200 body rather than a
 * status code — the same envelope the GIB hours dashboards use.
 */
function notAuthorizedMessage(value: unknown): string | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  if (record.status !== "dashboardNotAuthorized") return null;
  return typeof record.message === "string" ? record.message : "The Deal Tracker is not available for your account.";
}

export const DealTrackerApi = {
  /**
   * The M&A widget rows the caller can see. A Junior Banker gets exactly their
   * own row; it carries the Deal Tracker's task id plus the dynamic-field
   * addressing needed to read and write the table itself.
   *
   * Resolves to an empty list when the account has no M&A widget configured —
   * a permanent "not for you" answer, not a failure worth surfacing.
   */
  getMergersAndAcquisitions: async (type: MergersTrackerType): Promise<MergersAndAcquisitionsRow[]> => {
    try {
      const res = await apiClient.get<unknown>("/dashboard/mergersandacquisitions", { params: { type } });
      if (notAuthorizedMessage(res.data)) return [];
      return Array.isArray(res.data) ? (res.data as MergersAndAcquisitionsRow[]) : [];
    } catch (reason: unknown) {
      if (isAxiosError(reason) && reason.response) {
        const { status } = reason.response;
        if (status === 403 || notAuthorizedMessage(reason.response.data)) return [];
      }
      throw reason;
    }
  },

  /**
   * The Staffer's team in one call (TAD-6072) — both Junior Banker roles clubbed
   * into groups, so the two accordions cost one request rather than two.
   * Resolves to no groups when the account is not a Staffer.
   */
  getTeam: async (): Promise<MergersAndAcquisitionsRoleGroup[]> => {
    try {
      const res = await apiClient.get<unknown>("/dashboard/mergersandacquisitions/team");
      if (notAuthorizedMessage(res.data)) return [];
      const team = res.data as MergersAndAcquisitionsTeam | null;
      return Array.isArray(team?.Roles) ? team.Roles : [];
    } catch (reason: unknown) {
      if (isAxiosError(reason) && reason.response) {
        const { status } = reason.response;
        if (status === 403 || notAuthorizedMessage(reason.response.data)) return [];
      }
      throw reason;
    }
  },

  /**
   * Saves one Deal Tracker grid. Deliberately not the generic
   * "[POST] /dynamic-fields": this route checks the caller is a member of the
   * tracker Task — the same test behind AllowDealsEdit — where the generic
   * endpoint checks nothing at all.
   */
  saveDealTracker: async (payload: DynamicFieldSaveRequest): Promise<void> => {
    await apiClient.post("/dashboard/mergersAndAcquisitions/dealTracker", payload);
  },

  /**
   * "Indicate No Update" (TAD-6038): refreshes the Deal Tracker task's Last
   * Update and writes its audit entry without touching a single deal row.
   *
   * The route answers a refusal with a 403 (axios rejects) and success with HTTP
   * 200 + a `true` body — the shape Triyo.Web's dashboard header still reads. An
   * older API build answered a refusal with 200 and a plain `false` body instead,
   * which axios treats as success — so a refused No-Update would be shown to the
   * Banker as done. Reject that legacy `false` body explicitly so both the current
   * and the old contract fail loudly.
   */
  markNoUpdateRequired: async (taskId: number): Promise<void> => {
    const res = await apiClient.put<unknown>("/dashboard/mergersAndAcquisitions/updateMergersLastUpdated", null, {
      params: { taskId },
    });
    if (res.data === false) {
      throw new Error("You do not have access to this Deal Tracker.");
    }
  },
};
